import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Spin, message, Tag, Tooltip } from 'antd';
import ModalItem from './ModalItem';
import ButtonItem from './ButtonItem';
// El antd instalado es 4.15.6 y ahi Segmented todavia no existe (llego en 4.20); SegmentedItem
// es el wrapper de la casa sobre el Segmented del alias antd5.
import SegmentedItem from './SegmentedItem';
import dineroDisabledMask from './dineroDisabledMask';
import moment from 'moment';

// Historial de un insumo con un cliente. Lo abre el CLIC DERECHO en la misma celda del renglon
// de insumos donde el DOBLE CLIC abre DisponibilidadInsumoModal (su hermano estructural: mismo
// ModalItem, mismas tablas HTML crudas, mismo axios directo sin redux).
//
// Contrato de cierre: `onClose(precio?)`, igual que cerrarVerInsumo en AgregarVenta.js. Cerrar
// normal llama onClose(); "Usar este precio" llama onClose(precioNeto) y la pagina lo escribe en
// el renglon. El modal no sabe nada del formulario.
//
// La columna que importa es P.U. NETO = subtotal/cantidad. El `subtotal` guardado ya viene neto
// de descuentos, y `descuento` (%) y `descuentoCantidad` ($) son aditivos, asi que el neto es la
// unica cifra comparable entre renglones sin importar por cual de los dos mecanismos se descontó.
const TIPOS_TAG = {
  'Venta': 'green',
  'Pedido': 'blue',
  'Cotización': 'geekblue',
  'Embarque': 'purple',
};

const HistorialInsumoClienteModal = ({
  visible,
  onClose,
  insumoId,
  nombreInsumo,
  clienteId,
  excluirId,
  permitirUsarPrecio,
  headersToken,
  empresaId,
}) => {
  const [loading, setLoading] = useState(false);
  const [lineas, setLineas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [cliente, setCliente] = useState('');
  const [filtro, setFiltro] = useState('Todos');

  useEffect(() => {
    if (visible && insumoId && clienteId) {
      consultarHistorial();
    } else if (!visible) {
      // Limpiar al cerrar, si no parpadean los datos del insumo anterior al abrir el siguiente.
      setLineas([]);
      setResumen(null);
      setCliente('');
      setFiltro('Todos');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, insumoId, clienteId]);

  const consultarHistorial = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        'insumos/historialInsumoCliente',
        { empresaId, insumoId, clienteId, excluirId },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        setLineas(res.data.lineas || []);
        setResumen(res.data.resumen || null);
        setCliente(res.data.cliente || '');
      } else {
        message.warning('No se pudo cargar el historial');
      }
    } catch {
      message.warning('No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  const thStyle = {
    padding: '6px 10px',
    background: '#f5f5f5',
    fontWeight: 600,
    fontSize: 11,
    color: '#374151',
    borderBottom: '1px solid #e5e7eb',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  };

  const tdStyle = (align = 'left') => ({
    padding: '5px 10px',
    fontSize: 12,
    color: '#111827',
    borderBottom: '1px solid #f3f4f6',
    textAlign: align,
    whiteSpace: 'nowrap',
  });

  // Solo se muestra la columna de moneda si de verdad hay mas de una: si no, es una columna de
  // celdas identicas. Cuando se muestra, la abreviacion tambien va pegada al P.U. neto para que
  // un precio en dolares no se lea como pesos de un vistazo.
  const variasMonedas = useMemo(() => {
    const set = new Set(lineas.map((l) => l.moneda || ''));
    return set.size > 1;
  }, [lineas]);

  const opcionesFiltro = useMemo(() => {
    const presentes = ['Venta', 'Pedido', 'Cotización', 'Embarque'].filter((t) =>
      lineas.some((l) => l.tipo === t)
    );
    return ['Todos', ...presentes];
  }, [lineas]);

  const lineasFiltradas = useMemo(
    () => (filtro === 'Todos' ? lineas : lineas.filter((l) => l.tipo === filtro)),
    [lineas, filtro]
  );

  const totalColumnas = 11 + (variasMonedas ? 1 : 0) + (permitirUsarPrecio ? 1 : 0);

  // "10%", "$500", "10% + $500" o "—". Mostrar solo `descuento` diria "0%" en un renglon que si
  // tuvo $500 de descuento.
  const celdaDescuento = (l) => {
    if (l.precioOculto) return <span style={{ color: '#d1d5db' }}>—</span>;
    const tienePorcentaje = !!l.descuento;
    const tieneMonto = !!l.descuentoCantidad;
    if (!tienePorcentaje && !tieneMonto) return <span style={{ color: '#d1d5db' }}>—</span>;
    return (
      <span>
        {tienePorcentaje ? `${l.descuento}%` : null}
        {tienePorcentaje && tieneMonto ? ' + ' : null}
        {tieneMonto ? dineroDisabledMask(l.descuentoCantidad) : null}
      </span>
    );
  };

  const celdaNotas = (l) => {
    if (!l.leyenda && !l.observaciones) return <span style={{ color: '#d1d5db' }}>—</span>;
    return (
      <Tooltip
        title={
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {l.leyenda ? <div><b>Leyenda:</b> {l.leyenda}</div> : null}
            {l.observaciones ? <div><b>Obs.:</b> {l.observaciones}</div> : null}
          </div>
        }
      >
        <div
          style={{
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'help',
            color: '#6b7280',
          }}
        >
          {[l.leyenda, l.observaciones].filter(Boolean).join(' · ')}
        </div>
      </Tooltip>
    );
  };

  const chipResumen = (etiqueta, valor, extra, destacado) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, color: '#6b7280' }}>{etiqueta}</span>
      <strong style={{ fontSize: destacado ? 18 : 13, color: destacado ? '#00BFD6' : '#111827' }}>
        {valor}
      </strong>
      {extra ? <span style={{ fontSize: 10, color: '#9ca3af' }}>{extra}</span> : null}
    </div>
  );

  return (
    <ModalItem
      title={`Historial — ${nombreInsumo || ''}`}
      visible={visible}
      onCancel={() => onClose()}
      footer={null}
      width={1200}
      spinning={false}
    >
      <Spin spinning={loading}>
        {cliente ? (
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
            Cliente: <strong style={{ color: '#374151' }}>{cliente}</strong>
          </div>
        ) : null}

        {resumen && (
          <div
            style={{
              background: '#00BFD615',
              border: '1px solid #00BFD6',
              borderRadius: 8,
              padding: '10px 16px',
              marginBottom: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 32,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#005f6b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Basado en ventas
              </span>
              {resumen.monedasMezcladas && (
                <span style={{ fontSize: 10, color: '#b45309' }}>solo {resumen.moneda || 'una moneda'}</span>
              )}
            </div>
            {chipResumen(
              'Última venta',
              resumen.ultimaFecha ? moment(resumen.ultimaFecha).format('DD/MM/YY') : '—',
              resumen.ultimoFolio
            )}
            {chipResumen('Último P.U. neto', dineroDisabledMask(resumen.ultimoPrecioNeto), null, true)}
            {chipResumen(
              'P.U. promedio',
              dineroDisabledMask(resumen.precioPromedioPonderado),
              `min ${dineroDisabledMask(resumen.precioMin)} · max ${dineroDisabledMask(resumen.precioMax)}`
            )}
            {chipResumen('Cantidad total', resumen.cantidadTotal)}
            {chipResumen('Veces', resumen.veces)}
          </div>
        )}

        {opcionesFiltro.length > 2 && (
          <div style={{ marginBottom: 10 }}>
            <SegmentedItem options={opcionesFiltro} value={filtro} onChange={setFiltro} />
          </div>
        )}

        {lineas.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>
            Este cliente no tiene documentos anteriores con este insumo
          </div>
        )}

        {lineas.length > 0 && (
          // El minWidth es lo que produce el scroll horizontal; width:100% solo comprimiria las
          // columnas. El scroller va aqui dentro porque ModalItem ya mete su propio div con padding.
          <div
            style={{
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: '58vh',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
            }}
          >
            <table style={{ width: '100%', minWidth: 1050, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Fecha</th>
                  <th style={thStyle}>Tipo</th>
                  <th style={thStyle}>Folio</th>
                  <th style={thStyle}>Estado</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Cant.</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>P.U.</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Desc.</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>P.U. neto</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Subtotal</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                  {variasMonedas && <th style={thStyle}>Mon.</th>}
                  <th style={thStyle}>Notas</th>
                  {permitirUsarPrecio && <th style={thStyle}></th>}
                </tr>
              </thead>
              <tbody>
                {lineasFiltradas.length === 0 ? (
                  <tr>
                    <td
                      colSpan={totalColumnas}
                      style={{ textAlign: 'center', padding: '10px', color: '#9ca3af', fontSize: 12 }}
                    >
                      Sin registros
                    </td>
                  </tr>
                ) : (
                  lineasFiltradas.map((l, i) => (
                    <tr key={`${l.docId}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={tdStyle()}>{l.fecha ? moment(l.fecha).format('DD/MM/YY') : '-'}</td>
                      <td style={tdStyle()}>
                        <Tag color={TIPOS_TAG[l.tipo] || 'default'} className="square-tag">{l.tipo}</Tag>
                        {l.variante ? (
                          <div style={{ fontSize: 10, color: '#9ca3af' }}>{l.variante}</div>
                        ) : null}
                      </td>
                      <td style={tdStyle()}>{l.folio}</td>
                      <td style={tdStyle()}>
                        <Tag color={l.estadoColor} className="square-tag">{l.estadoLabel}</Tag>
                      </td>
                      <td style={tdStyle('right')}>{l.cantidad}</td>
                      <td style={tdStyle('right')}>
                        {l.precioOculto ? '—' : dineroDisabledMask(l.precioUnitario)}
                      </td>
                      <td style={tdStyle('right')}>{celdaDescuento(l)}</td>
                      <td style={{ ...tdStyle('right'), fontWeight: 700, color: '#00BFD6' }}>
                        {l.precioOculto ? '—' : dineroDisabledMask(l.precioNeto)}
                        {variasMonedas && !l.precioOculto ? (
                          <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 3 }}>{l.moneda}</span>
                        ) : null}
                      </td>
                      <td style={tdStyle('right')}>
                        {l.precioOculto ? '—' : dineroDisabledMask(l.subtotal)}
                      </td>
                      <td style={tdStyle('right')}>
                        {l.precioOculto ? '—' : dineroDisabledMask(l.total)}
                      </td>
                      {variasMonedas && <td style={tdStyle()}>{l.moneda}</td>}
                      <td style={tdStyle()}>{celdaNotas(l)}</td>
                      {permitirUsarPrecio && (
                        <td style={tdStyle()}>
                          {!l.precioOculto && l.precioNeto > 0 ? (
                            // Se manda el NETO, no precioUnitario: es el que de verdad se cobro.
                            // La pagina lo escribe como precio unitario y limpia el descuento del
                            // renglon, para que lo cobrado sea exactamente este numero.
                            <span title="Lo aplica como precio unitario y limpia el descuento del renglón, porque este precio ya viene neto">
                              <ButtonItem
                                blanco
                                lessTime
                                style={{ fontSize: 11 }}
                                onClick={() => onClose(l.precioNeto)}
                              >
                                Usar este precio
                              </ButtonItem>
                            </span>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Spin>
    </ModalItem>
  );
};

export default HistorialInsumoClienteModal;
